/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useMemo } from 'react';

import {
  IntelligenceType,
  type IntelligenceData,
} from '@coze-arch/idl/intelligence_api';

import { type LibraryResourceMetrics } from './use-library-resources-count';

export interface DevelopMetrics {
  agentCount: number;
  draftAgentCount: number;
  ontologyAppCount: number;
  publishedAppCount: number;
  publishedAppLoading: boolean;
  libraryResources: LibraryResourceMetrics;
}

interface UseDevelopMetricsOptions {
  list?: IntelligenceData[];
  ontologyAppCount?: number;
  publishedAppCount?: number;
  publishedAppLoading?: boolean;
  libraryResources?: LibraryResourceMetrics;
}

const DEFAULT_LIBRARY_RESOURCES: LibraryResourceMetrics = {
  total: 0,
  publishedCount: 0,
  draftCount: 0,
  knowledgeCount: 0,
  pluginCount: 0,
  workflowCount: 0,
  imageflowCount: 0,
  promptCount: 0,
  loading: false,
};

export function useDevelopMetrics({
  list = [],
  ontologyAppCount = 0,
  publishedAppCount = 0,
  publishedAppLoading = false,
  libraryResources = DEFAULT_LIBRARY_RESOURCES,
}: UseDevelopMetricsOptions): DevelopMetrics {
  return useMemo(() => {
    const agents = list.filter(item => item.type === IntelligenceType.Bot);
    const agentCount = agents.length;
    const publishedAgentCount = agents.filter(
      item => item.publish_info?.has_published,
    ).length;
    const draftAgentCount = agentCount - publishedAgentCount;

    return {
      agentCount,
      draftAgentCount,
      ontologyAppCount,
      publishedAppCount,
      publishedAppLoading,
      libraryResources,
    };
  }, [
    list,
    ontologyAppCount,
    publishedAppCount,
    publishedAppLoading,
    libraryResources,
  ]);
}
